import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { ConfirmBookingInput, ModelType, PaymentStatus } from "@/gql/graphql";
import { Payment } from "@/gql/schema";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { TrashIcon } from "@heroicons/react/24/outline";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { confirmBookingMutation } from "@/app/Bookings/GraphQL/cancelBookingMutation";
import { useCallback } from "react";
import confetti from "canvas-confetti";
import { confirmBookingValidation } from "@/app/Bookings/Validation/bookingChangeStatusSchema";
import { useIsChristmas } from "@/lib/Components/Layout/Christmas";
import { PaymentItem } from "@/app/Bookings/Components/PaymentItem";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";

type BookingConfirmDialogProps = {
  bookingId: string;
};
export function BookingConfirmDialog({ bookingId }: BookingConfirmDialogProps) {
  const { mutateAsync } = useGqlMutation(confirmBookingMutation);
  const { close } = useDialogState();
  const { isAdmin } = useTenant();

  const booking = useBooking(bookingId);
  const isChristmas = useIsChristmas();
  const fireConfetti = useFireConfetti(isChristmas);

  const payNowTotal = booking.charges.reduce((acc, charge) => {
    return acc + charge.platform_amount_collectable;
  }, 0);

  //Sum the customer payments
  const totalPaid = booking.customerPayments.reduce((acc, payment) => {
    if (payment.status === PaymentStatus.Paid) {
      acc += payment.amount;
    }

    return acc;
  }, 0);

  const pendingPayments = booking.customerPayments.filter((payment) => {
    return (
      payment.status === PaymentStatus.Pending &&
      payment.fulfilledBy?.__typename === "StripePayment" &&
      (payment.fulfilledBy.attributed_to_type === ModelType.Booking ||
        payment.fulfilledBy.attributed_to_type ===
          ModelType.BookingPaymentRequest)
    );
  });

  return (
    <BaseDialog title="Confirm booking">
      {isAdmin ? (
        <div className="mb-6 space-y-6">
          <div>
            {pendingPayments.length > 0 ? (
              <div className="grid w-full gap-6 md:grid-cols-2">
                {pendingPayments.map((payment) => {
                  //Only show the pending payments for the booking
                  return <PaymentItem payment={payment} key={payment.id} />;
                })}
              </div>
            ) : (
              <WarningBanner>
                There are no pending payments for this booking
              </WarningBanner>
            )}
          </div>
          <ul className="space-y-1.5">
            <li>
              <span className="">Due: </span>
              {formatCurrency(payNowTotal, booking.currency)}
            </li>
            {totalPaid ? (
              <li>
                <span className="">Total paid: </span>
                {formatCurrency(totalPaid, booking.currency)}
              </li>
            ) : null}
            <li className="">
              <span className="font-bold">Remaining: </span>
              {formatCurrency(payNowTotal - totalPaid, booking.currency)}
            </li>
          </ul>
        </div>
      ) : null}

      <GenericForm<ConfirmBookingInput>
        validate={toFormikValidate(confirmBookingValidation)}
        initialValues={{
          customer_note: booking.customer_note,
          supplier_reference: booking.supplier_reference,
        }}
        onSubmit={async (values) => {
          fireConfetti();

          await mutateAsync({
            id: booking.id,
            input: values,
          });

          await close();
        }}
      >
        <TextInput
          name="supplier_reference"
          label={`${booking.supplier.name} reference`}
          autoFocus
        />

        <RichTextInput
          tooltip="This will appear on the customers booking pdf"
          name="customer_note"
          label="Customer note"
          className="col-span-full"
          optionalLabel
        />

        {booking.relocation.is_ferry_required ? (
          <>
            <WarningBanner className="col-span-full mb-6">
              This booking has a ferry crossing. Please add the ferry
              confirmation (if {"it's"} included) and any other relevant
              information to the ferry note.
            </WarningBanner>
          </>
        ) : null}

        <DialogSubmitButton text="Confirm" />
      </GenericForm>
    </BaseDialog>
  );
}

const christmasShapes = [
  confetti.shapeFromText({ text: "🎄", scalar: 2 }),
  confetti.shapeFromText({ text: "🎁", scalar: 2 }),
  confetti.shapeFromText({ text: "☃️", scalar: 2 }),
  confetti.shapeFromText({ text: "❄️", scalar: 2 }),
  confetti.shapeFromText({ text: "🦌", scalar: 2 }),
];

function useFireConfetti(isChristmas: boolean) {
  return useCallback(() => {
    if (isChristmas) {
      const end = Date.now() + 2000;
      let frameCount = 0;

      const frame = () => {
        if (Date.now() > end) return;
        frameCount++;

        // Left side burst
        confetti({
          particleCount: 1,
          angle: 60,
          spread: 55,
          startVelocity: 50,
          zIndex: 9999,
          origin: { x: 0, y: 0.6 },
          shapes: christmasShapes,
          scalar: 2,
          flat: true,
        });

        // Right side burst
        confetti({
          particleCount: 1,
          angle: 120,
          spread: 55,
          startVelocity: 50,
          zIndex: 9999,
          origin: { x: 1, y: 0.6 },
          shapes: christmasShapes,
          scalar: 2,
          flat: true,
        });

        // Top snowfall (every 2nd frame)
        if (frameCount % 2 === 0) {
          confetti({
            particleCount: 1,
            angle: 270,
            spread: 120,
            startVelocity: 20,
            zIndex: 9999,
            origin: { x: Math.random(), y: 0 },
            shapes: [confetti.shapeFromText({ text: "❄️", scalar: 2 })],
            gravity: 0.4,
            scalar: 2,
            drift: 1,
            flat: true,
          });
        }

        requestAnimationFrame(frame);
      };

      frame();
    } else {
      const end = Date.now() + 1000;

      const frame = () => {
        if (Date.now() > end) return;

        confetti({
          particleCount: 4,
          angle: 60,
          spread: 65,
          startVelocity: 45,
          zIndex: 9999,
          origin: { x: 0, y: 0.5 },
          colors: ["#FF0000", "#00FF00", "#0000FF", "#FF00FF"],
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 65,
          startVelocity: 45,
          zIndex: 9999,
          origin: { x: 1, y: 0.5 },
          colors: ["#FF0000", "#00FF00", "#0000FF", "#FF00FF"],
        });

        requestAnimationFrame(frame);
      };

      frame();
    }
  }, [isChristmas]);
}

export function ChangeRequestWarning({ payment }: { payment: Payment }) {
  return (
    <div className="rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="shrink-0">
          <ExclamationTriangleIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">
            Attention needed
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              This payment is part of a change request. Please ensure you do one
              of the following:
            </p>
            <ul className="mt-1 list-inside list-decimal">
              <li>
                Capture the payment to <strong>approve</strong> the change
                request
              </li>
              <li>
                Capture{" "}
                {formatCurrency(0, payment.currency, {
                  showZero: true,
                })}{" "}
                to <strong>decline</strong> the change request
              </li>
              <li>
                Leave the change request as <strong>pending</strong> by removing{" "}
                <TrashIcon className="inline h-4 w-4" /> the payment
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
